import React, { useState, useEffect } from 'react';
import { 
  Clock, 
  Calendar, 
  CheckCircle, 
  XCircle, 
  AlertCircle,
  User,
  BarChart3,
  Download,
  MessageSquare,
  MapPin,
  Edit3
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { AttendanceRecord } from '../types/auth';
import { dbHelpers } from '../config/database';

const AttendanceSystem = () => {
  const { user } = useAuth();
  const [attendanceRecords, setAttendanceRecords] = useState<AttendanceRecord[]>([]);
  const [todayRecord, setTodayRecord] = useState<AttendanceRecord | null>(null);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [loading, setLoading] = useState(true);
  const [showLateReasonModal, setShowLateReasonModal] = useState(false);
  const [showEarlyLeaveModal, setShowEarlyLeaveModal] = useState(false);
  const [showWorkingStatusModal, setShowWorkingStatusModal] = useState(false);
  const [lateReason, setLateReason] = useState('');
  const [earlyLeaveReason, setEarlyLeaveReason] = useState('');
  const [workingStatus, setWorkingStatus] = useState('');
  const [currentLocation, setCurrentLocation] = useState<string>('');

  // Office hours: 10:00 AM to 6:00 PM
  const OFFICE_START_TIME = '10:00:00';
  const OFFICE_END_TIME = '18:00:00';

  useEffect(() => {
    // Update current time every second
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (user) {
      loadAttendanceRecords();
      getCurrentLocation();
    }
  }, [user]);

  const downloadAttendanceCSV = () => {
  if (!attendanceRecords.length) {
    alert('No attendance data to export');
    return;
  }

  const headers = [
    'Date',
    'Check In',
    'Check Out',
    'Working Hours',
    'Status',
    'Work Status',
    'Late Reason',
    'Early Leave Reason',
    'Check-in Location',
    'Check-out Location',
  ];

  const rows = attendanceRecords.map((r) => [
    r.date,
    formatTime(r.checkIn),
    formatTime(r.checkOut || ''),
    calculateWorkingHours(r.checkIn, r.checkOut),
    r.status,
    r.workingStatus || '',
    r.lateReason || '',
    r.earlyLeaveReason || '',
    r.location?.checkInLocation || '',
    r.location?.checkOutLocation || '',
  ]);

  const csvContent =
    [headers, ...rows]
      .map((row) =>
        row
          .map((item) => `"${String(item).replace(/"/g, '""')}"`)
          .join(',')
      )
      .join('\n');

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);

  const link = document.createElement('a');
  link.href = url;
  link.download = `attendance-report-${user?.name}-${new Date()
    .toISOString()
    .split('T')[0]}.csv`;

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};


  const getCurrentLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const { latitude, longitude } = position.coords;
          try {
            // Use a reverse geocoding service to get location name
            // For demo purposes, we'll use a simple location string
            setCurrentLocation(`Lat: ${latitude.toFixed(4)}, Lng: ${longitude.toFixed(4)}`);
          } catch (error) {
            setCurrentLocation('Location unavailable');
          }
        },
        (error) => {
          console.error('Error getting location:', error);
          setCurrentLocation('Location permission denied');
        }
      );
    } else {
      setCurrentLocation('Geolocation not supported');
    }
  };
const loadAttendanceRecords = async () => {
  try {
    setLoading(true);

    const res = await dbHelpers.getUserAttendance(user.id);

    // ✅ MUST extract array
    //const records = Array.isArray(res?.data) ? res.data : [];


const records = Array.isArray(res?.data)
  ? res.data.map((r: any) => ({
      ...r,
      workingStatus: r.working_status ?? r.workingStatus ?? '',
      lateReason: r.late_reason ?? r.lateReason ?? '',
      earlyLeaveReason: r.early_leave_reason ?? r.earlyLeaveReason ?? '',
      checkIn: r.check_in ?? r.checkIn,
      checkOut: r.check_out ?? r.checkOut,
      location: {
        checkInLocation: r.check_in_location ?? r.location?.checkInLocation,
        checkOutLocation: r.check_out_location ?? r.location?.checkOutLocation,
      },
    }))
  : [];










    console.log('RECORDS:', records);

    setAttendanceRecords(records);

    const today = new Date().toISOString().split('T')[0];

    const todayRec = records.find(r => r.date === today);

    setTodayRecord(todayRec || null);

  } catch (e) {
    console.error(e);
  } finally {
    setLoading(false);
  }
};


  

  const isLate = (checkInTime: string) => {
    const checkIn = new Date(`2000-01-01T${checkInTime}`);
    const officeStart = new Date(`2000-01-01T${OFFICE_START_TIME}`);
    return checkIn > officeStart;
  };

  const isEarlyLeave = (checkOutTime: string) => {
    const checkOut = new Date(`2000-01-01T${checkOutTime}`);
    const officeEnd = new Date(`2000-01-01T${OFFICE_END_TIME}`);
    return checkOut < officeEnd;
  };
const formatTime = (value?: string) => {
  if (
    !value ||
    value === '0000-00-00 00:00:00'
  ) {
    return '-';
  }

  // Handle both datetime strings and time-only strings
  let timeStr = value;
  if (value.includes(' ')) {
    timeStr = value.split(' ')[1];
  }

  const date = new Date(`2000-01-01T${timeStr}`);
  if (isNaN(date.getTime())) return '-';

  return date.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });
};

const calculateWorkingHours = (checkIn?: string, checkOut?: string) => {
  if (
    !checkIn ||
    !checkOut ||
    checkIn === '0000-00-00 00:00:00' ||
    checkOut === '0000-00-00 00:00:00'
  ) {
    return '-';
  }

  const inTime = checkIn.split(' ')[1];
  const outTime = checkOut.split(' ')[1];

  const start = new Date(`2000-01-01T${inTime}`);
  const end = new Date(`2000-01-01T${outTime}`);

  if (isNaN(start.getTime()) || isNaN(end.getTime())) return '-';

  const diff = (end.getTime() - start.getTime()) / 36e5;
  return `${diff.toFixed(1)}h`;
};


  const handleCheckIn = async () => {
  if (!user) return;

  if (todayRecord) {
    alert('You have already checked in today.');
    return;
  }

  const now = new Date();
  const time = now.toTimeString().split(' ')[0];

  if (isLate(time)) {
    setShowLateReasonModal(true);
    return;
  }

  await processCheckIn(time, 'present', '');
};

 const processCheckIn = async (time: string, status: string, reason: string) => {
  if (!user) return;

  const today = new Date().toISOString().split('T')[0];

  try {
    const res = await dbHelpers.checkIn({
      user_id: user.id,
      date: today,
      check_in: time,
      status,
      late_reason: reason || null,
      working_status: 'present',
      check_in_location: currentLocation
    });

    const responseData = res?.data ?? res;

    // ✅ If already checked in → just refresh UI
    if (responseData?.status === false) {
      await loadAttendanceRecords();
      setShowLateReasonModal(false);
      return;
    }

    // ✅ Success
    setShowLateReasonModal(false);
    setLateReason('');
    await loadAttendanceRecords();

  } catch (error) {
    console.error('Check-in error:', error);
  }
};



  const handleLateCheckIn = async () => {
    if (!lateReason.trim()) {
      alert('Please provide a reason for being late.');
      return;
    }

    const now = new Date();
    const time = now.toTimeString().split(' ')[0];
    await processCheckIn(time, 'late', lateReason);
  };

  const handleCheckOut = async () => {
    if (!user || !todayRecord) return;
    
    const now = new Date();
    const time = now.toTimeString().split(' ')[0];
    
    // Check if employee is leaving early
    if (isEarlyLeave(time)) {
      setShowEarlyLeaveModal(true);
      return;
    }

    await processCheckOut(time, '');
  };

  const processCheckOut = async (time: string, reason: string) => {
    if (!user || !todayRecord) return;

    const today = new Date().toISOString().split('T')[0];
    
    try {
      await dbHelpers.updateAttendanceCheckout(user.id, today, time, reason, currentLocation, todayRecord.id);
      
      /*const updatedRecord = {
        ...todayRecord,
        checkOut: time,
        earlyLeaveReason: reason,
        location: {
          ...todayRecord.location,
          checkOutLocation: currentLocation,
          checkOutCoords: { lat: 0, lng: 0 }
        }
      };*/




      const updatedRecord = {
  ...todayRecord,
  checkOut: `${today} ${time}`,
  earlyLeaveReason: reason,
  location: {
    ...todayRecord.location,
    checkOutLocation: currentLocation,
  },
};

      
      setTodayRecord(updatedRecord);
      await loadAttendanceRecords();
      setShowEarlyLeaveModal(false);
      setEarlyLeaveReason('');
    } catch (error) {
      console.error('Error checking out:', error);
      alert('Error recording check-out. Please try again.');
    }
  };

  const handleEarlyCheckOut = async () => {
    if (!earlyLeaveReason.trim()) {
      alert('Please provide a reason for leaving early.');
      return;
    }

    const now = new Date();
    const time = now.toTimeString().split(' ')[0];
    await processCheckOut(time, earlyLeaveReason);
  };

  const handleUpdateWorkingStatus = async () => {
    if (!user || !todayRecord || !workingStatus.trim()) {
      alert('Please enter your working status for today.');
      return;
    }

    try {
      console.log('Calling updateAttendanceWorkingStatus with:', {
        userId: user.id,
        date: todayRecord.date,
        workingStatus,
      });

      const result = await dbHelpers.updateAttendanceWorkingStatus(user.id, todayRecord.date, workingStatus);
      
      console.log('Response:', result);

      if (!result || !result.status) {
        alert(result?.message || 'Error updating working status. Please try again.');
        return;
      }
      
      const updatedRecord = {
        ...todayRecord,
        workingStatus: workingStatus
      };
      
      setTodayRecord(updatedRecord);
      await loadAttendanceRecords();
      setShowWorkingStatusModal(false);
      setWorkingStatus('');
      alert('Working status updated successfully!');
    } catch (error) {
      console.error('Error updating working status:', error);
      alert('Error updating working status: ' + (error instanceof Error ? error.message : String(error)));
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'present': return 'text-green-600 bg-green-50';
      case 'late': return 'text-yellow-600 bg-yellow-50';
      case 'absent': return 'text-red-600 bg-red-50';
      case 'half-day': return 'text-blue-600 bg-blue-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'present': return <CheckCircle className="h-4 w-4" />;
      case 'late': return <AlertCircle className="h-4 w-4" />;
      case 'absent': return <XCircle className="h-4 w-4" />;
      default: return <Clock className="h-4 w-4" />;
    }
  };

  const calculateStats = () => {
  if (!Array.isArray(attendanceRecords)) {
    return {
      totalDays: 0,
      presentDays: 0,
      lateDays: 0,
      absentDays: 0,
    };
  }

  const totalDays = attendanceRecords.length;
  const presentDays = attendanceRecords.filter(r => r.status === 'present').length;
  const lateDays = attendanceRecords.filter(r => r.status === 'late').length;
  const absentDays = attendanceRecords.filter(r => r.status === 'absent').length;

  return { totalDays, presentDays, lateDays, absentDays };
};


  const stats = calculateStats();

  if (!user) {
    return (
      <div className="text-center py-12">
        <User className="h-16 w-16 text-gray-400 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">Please Login</h3>
        <p className="text-gray-600">You need to login to access the attendance system.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-primary mx-auto mb-4"></div>
        <p className="text-gray-600">Loading attendance data...</p>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-6 space-y-8">
      {/* User Profile Section */}
      

      {/* Header */}
      <div className="text-center">
        <h1 className="text-3xl font-bold text-brand-secondary mb-2">Attendance System</h1>
        <p className="text-gray-600">Welcome back, {user.name}!</p>
        <div className="text-2xl font-mono text-brand-primary mt-2">
          {currentTime.toLocaleTimeString()}
        </div>
        <div className="text-sm text-gray-500 mt-1">
          Office Hours: 10:00 AM - 6:00 PM
        </div>
        {currentLocation && (
          <div className="flex items-center justify-center text-sm text-gray-500 mt-1">
            <MapPin className="h-4 w-4 mr-1" />
            {currentLocation}
          </div>
        )}
      </div>

      {/* Onboarding Checklist Widget */}
      {user.role_id === 3 && (
        <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-200">
          <h2 className="text-xl font-semibold text-brand-secondary mb-2 flex items-center">
            <CheckCircle className="h-5 w-5 mr-2 text-brand-primary animate-pulse" />
            Your Employee Onboarding Steps
          </h2>
          <p className="text-sm text-gray-500 mb-6 font-medium">
            Complete these initial steps to finalize your onboarding setup with our team.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {[
              { label: 'Set Profile Details', desc: 'Fill address and contacts in Profile', done: !!user.phone && !!user.address, link: '#profile' },
              { label: 'Submit Onboarding Documents', desc: 'Upload documents for HR audit', done: true, link: '#profile' },
              { label: 'Complete Daily Check-in', desc: 'Log check-in location', done: !!todayRecord, link: '#attendance' },
              { label: 'Check Assigned Board Tasks', desc: 'Review active issues and sprints', done: false, link: '#board' }
            ].map((step, idx) => (
              <a
                href={step.link}
                key={idx}
                className={`p-4 rounded-lg border transition-all duration-200 block hover:shadow-md ${
                  step.done 
                    ? 'bg-green-50/50 border-green-200 text-green-800' 
                    : 'bg-gray-50 border-gray-200 text-gray-800 hover:border-brand-primary'
                }`}
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-bold uppercase tracking-wider text-gray-400">
                    Step {idx + 1}
                  </span>
                  {step.done ? (
                    <CheckCircle className="h-4 w-4 text-green-600 animate-bounce" />
                  ) : (
                    <div className="h-4 w-4 rounded-full border-2 border-gray-300" />
                  )}
                </div>
                <h4 className="font-semibold text-sm mb-1">{step.label}</h4>
                <p className="text-xs text-gray-400">{step.desc}</p>
              </a>
            ))}
          </div>
        </div>
      )}

      {/* Today's Attendance */}
      <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-200">
        <h2 className="text-xl font-semibold text-brand-secondary mb-4 flex items-center">
          <Calendar className="h-5 w-5 mr-2" />
          Today's Attendance - {new Date().toLocaleDateString()}
        </h2>
        
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-4">
            {todayRecord ? (
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium">Status:</span>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium flex items-center ${getStatusColor(todayRecord.status)}`}>
                        {getStatusIcon(todayRecord.status)}
                        <span className="ml-1 capitalize">{todayRecord.status}</span>
                      </span>
                    </div>
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium">Check In:</span>
                      <span className={`font-mono ${todayRecord.status === 'late' ? 'text-yellow-600' : 'text-green-600'}`}>
                        {formatTime(todayRecord.checkIn)}
                      </span>
                    </div>
                    {todayRecord.checkOut && (
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-medium">Check Out:</span>
                        <span className="text-red-600 font-mono">{formatTime(todayRecord.checkOut)}</span>
                      </div>
                    )}
                    {todayRecord.checkIn && todayRecord.checkOut && (
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-medium">Working Hours:</span>
                        <span className="text-brand-primary font-mono">
                          {calculateWorkingHours(todayRecord.checkIn, todayRecord.checkOut)}
                        </span>
                      </div>
                    )}
                  </div>
                  
                  <div>
                    {todayRecord.location?.checkInLocation && (
                      <div className="mb-2">
                        <span className="font-medium text-sm">Check-in Location:</span>
                        <p className="text-xs text-gray-600 flex items-center">
                          <MapPin className="h-3 w-3 mr-1" />
                          {todayRecord.location.checkInLocation}
                        </p>
                      </div>
                    )}
                    {todayRecord.location?.checkOutLocation && (
                      <div className="mb-2">
                        <span className="font-medium text-sm">Check-out Location:</span>
                        <p className="text-xs text-gray-600 flex items-center">
                          <MapPin className="h-3 w-3 mr-1" />
                          {todayRecord.location.checkOutLocation}
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                {todayRecord.lateReason && (
                  <div className="mt-3 p-2 bg-yellow-50 rounded border-l-4 border-yellow-400">
                    <span className="text-sm font-medium text-yellow-800">Late Reason:</span>
                    <p className="text-sm text-yellow-700">{todayRecord.lateReason}</p>
                  </div>
                )}
                {todayRecord.earlyLeaveReason && (
                  <div className="mt-3 p-2 bg-blue-50 rounded border-l-4 border-blue-400">
                    <span className="text-sm font-medium text-blue-800">Early Leave Reason:</span>
                    <p className="text-sm text-blue-700">{todayRecord.earlyLeaveReason}</p>
                  </div>
                )}
                {todayRecord.workingStatus && (
                  <div className="mt-3 p-3 bg-green-50 rounded border-l-4 border-green-400">
                    <span className="text-sm font-medium text-green-800">Today's Work Status:</span>
                    <p className="text-sm text-green-700 mt-1 font-semibold">{todayRecord.workingStatus}</p>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-8">
                <Clock className="h-12 w-12 text-gray-400 mx-auto mb-2" />
                <p className="text-gray-600">No attendance recorded for today</p>
              </div>
            )}
          </div>
          
          <div className="flex flex-col justify-center space-y-3">
            {!todayRecord ? (
              <button
                onClick={handleCheckIn}
                className="bg-green-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-green-700 transition-all duration-200 flex items-center justify-center transform hover:scale-105"
              >
                <CheckCircle className="h-5 w-5 mr-2" />
                Check In
              </button>
            ) : !todayRecord.checkOut ? (
              <>
                <button
                  onClick={handleCheckOut}
                  className="bg-red-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-red-700 transition-all duration-200 flex items-center justify-center transform hover:scale-105"
                >
                  <XCircle className="h-5 w-5 mr-2" />
                  Check Out
                </button>
                <button
                  onClick={() => {
                    setWorkingStatus(todayRecord.workingStatus || '');
                    setShowWorkingStatusModal(true);
                  }}
                  className="bg-brand-primary text-white py-2 px-4 rounded-lg font-medium hover:bg-brand-dark transition-all duration-200 flex items-center justify-center text-sm"
                >
                  <Edit3 className="h-4 w-4 mr-2" />
                  {todayRecord.workingStatus ? 'Update' : 'Add'} Work Status
                </button>
              </>
            ) : (
              <>
                <div className="text-center py-3 text-green-600 font-medium">
                  ✓ Attendance completed for today
                </div>
                <button
                  onClick={() => {
                    setWorkingStatus(todayRecord.workingStatus || '');
                    setShowWorkingStatusModal(true);
                  }}
                  className="bg-brand-primary text-white py-2 px-4 rounded-lg font-medium hover:bg-brand-dark transition-all duration-200 flex items-center justify-center text-sm"
                >
                  <Edit3 className="h-4 w-4 mr-2" />
                  {todayRecord.workingStatus ? 'Update' : 'Add'} Work Status
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Monthly Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white rounded-lg shadow-md p-6 text-center border border-gray-200">
          <div className="text-2xl font-bold text-brand-primary mb-2">{stats.totalDays}</div>
          <div className="text-sm text-gray-600">Total Days</div>
        </div>
        <div className="bg-white rounded-lg shadow-md p-6 text-center border border-gray-200">
          <div className="text-2xl font-bold text-green-600 mb-2">{stats.presentDays}</div>
          <div className="text-sm text-gray-600">Present</div>
        </div>
        <div className="bg-white rounded-lg shadow-md p-6 text-center border border-gray-200">
          <div className="text-2xl font-bold text-yellow-600 mb-2">{stats.lateDays}</div>
          <div className="text-sm text-gray-600">Late</div>
        </div>
        <div className="bg-white rounded-lg shadow-md p-6 text-center border border-gray-200">
          <div className="text-2xl font-bold text-red-600 mb-2">{stats.absentDays}</div>
          <div className="text-sm text-gray-600">Absent</div>
        </div>
      </div>

      {/* Attendance History */}
      <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-200">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-brand-secondary flex items-center">
            <BarChart3 className="h-5 w-5 mr-2" />
            Attendance History
          </h2>
         <button
  onClick={downloadAttendanceCSV}
  className="text-brand-primary hover:text-brand-dark flex items-center text-sm"
>
  <Download className="h-4 w-4 mr-1" />
  Export
</button>

        </div>
        
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="text-left py-3 px-4">Date</th>
                <th className="text-left py-3 px-4">Check In</th>
                <th className="text-left py-3 px-4">Check Out</th>
                <th className="text-left py-3 px-4">Hours</th>
                <th className="text-left py-3 px-4">Status</th>
                <th className="text-left py-3 px-4">Location</th>
                <th className="text-left py-3 px-4">Work Status</th>
                <th className="text-left py-3 px-4">Reasons</th>
              </tr>
            </thead>
            <tbody>
              {attendanceRecords.slice(0, 10).map((record) => (
                <tr key={record.id} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="py-3 px-4">{new Date(record.date).toLocaleDateString()}</td>
                  <td className="py-3 px-4 font-mono text-green-600">{formatTime(record.checkIn)}</td>
                  <td className="py-3 px-4 font-mono text-red-600">{formatTime(record.checkOut || '')}</td>
                  <td className="py-3 px-4 font-mono text-brand-primary">
                    {calculateWorkingHours(record.checkIn, record.checkOut)}
                  </td>
                  <td className="py-3 px-4">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium flex items-center w-fit ${getStatusColor(record.status)}`}>
                      {getStatusIcon(record.status)}
                      <span className="ml-1 capitalize">{record.status}</span>
                    </span>
                  </td>
                  <td className="py-3 px-4">
                    <div className="space-y-1">
                      {record.location?.checkInLocation && (
                        <div className="text-xs text-gray-600 flex items-center">
                          <MapPin className="h-3 w-3 mr-1 text-green-500" />
                          In: {record.location.checkInLocation.substring(0, 20)}...
                        </div>
                      )}
                      {record.location?.checkOutLocation && (
                        <div className="text-xs text-gray-600 flex items-center">
                          <MapPin className="h-3 w-3 mr-1 text-red-500" />
                          Out: {record.location.checkOutLocation.substring(0, 20)}...
                        </div>
                      )}
                      {!record.location?.checkInLocation && !record.location?.checkOutLocation && (
                        <span className="text-gray-400">-</span>
                      )}
                    </div>
                  </td>
                  <td className="py-3 px-4">
                    {record.workingStatus ? (
                      <div className="text-xs text-gray-700 bg-gray-100 px-2 py-1 rounded max-w-32 truncate" title={record.workingStatus}>
                        {record.workingStatus}
                      </div>
                    ) : (
                      <span className="text-gray-400">-</span>
                    )}
                  </td>
                  <td className="py-3 px-4">
                    <div className="space-y-1">
                      {record.lateReason && (
                        <div className="text-xs text-yellow-600 bg-yellow-50 px-2 py-1 rounded">
                          Late: {record.lateReason}
                        </div>
                      )}
                      {record.earlyLeaveReason && (
                        <div className="text-xs text-blue-600 bg-blue-50 px-2 py-1 rounded">
                          Early: {record.earlyLeaveReason}
                        </div>
                      )}
                      {!record.lateReason && !record.earlyLeaveReason && (
                        <span className="text-gray-400">-</span>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          
          {attendanceRecords.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              No attendance records found
            </div>
          )}
        </div>
      </div>

      {/* Late Reason Modal */}
      {showLateReasonModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6">
            <div className="flex items-center mb-4">
              <AlertCircle className="h-6 w-6 text-yellow-500 mr-2" />
              <h3 className="text-lg font-semibold text-gray-900">Late Check-in</h3>
            </div>
            <p className="text-gray-600 mb-4">
              You're checking in after 10:00 AM. Please provide a reason for being late.
            </p>
            <textarea
              value={lateReason}
              onChange={(e) => setLateReason(e.target.value)}
              placeholder="Enter reason for being late..."
              className="w-full p-3 border border-gray-300 rounded-lg resize-none focus:ring-2 focus:ring-brand-primary focus:border-transparent"
              rows={3}
            />
            <div className="flex gap-3 mt-4">
              <button
                onClick={handleLateCheckIn}
                className="flex-1 bg-yellow-600 text-white py-2 px-4 rounded-lg font-medium hover:bg-yellow-700 transition-colors duration-200"
              >
                Check In Late
              </button>
              <button
                onClick={() => {
                  setShowLateReasonModal(false);
                  setLateReason('');
                }}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors duration-200"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Early Leave Modal */}
      {showEarlyLeaveModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6">
            <div className="flex items-center mb-4">
              <MessageSquare className="h-6 w-6 text-blue-500 mr-2" />
              <h3 className="text-lg font-semibold text-gray-900">Early Check-out</h3>
            </div>
            <p className="text-gray-600 mb-4">
              You're checking out before 6:00 PM. Please provide a reason for leaving early.
            </p>
            <textarea
              value={earlyLeaveReason}
              onChange={(e) => setEarlyLeaveReason(e.target.value)}
              placeholder="Enter reason for leaving early..."
              className="w-full p-3 border border-gray-300 rounded-lg resize-none focus:ring-2 focus:ring-brand-primary focus:border-transparent"
              rows={3}
            />
            <div className="flex gap-3 mt-4">
              <button
                onClick={handleEarlyCheckOut}
                className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-lg font-medium hover:bg-blue-700 transition-colors duration-200"
              >
                Check Out Early
              </button>
              <button
                onClick={() => {
                  setShowEarlyLeaveModal(false);
                  setEarlyLeaveReason('');
                }}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors duration-200"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Working Status Modal */}
      {showWorkingStatusModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6">
            <div className="flex items-center mb-4">
              <Edit3 className="h-6 w-6 text-brand-primary mr-2" />
              <h3 className="text-lg font-semibold text-gray-900">Daily Working Status</h3>
            </div>
            <p className="text-gray-600 mb-4">
              Please describe what you worked on today or your current tasks.
            </p>
            <textarea
              value={workingStatus}
              onChange={(e) => setWorkingStatus(e.target.value)}
              placeholder="e.g., Completed user authentication module, Fixed bugs in payment system, Attended client meeting..."
              className="w-full p-3 border border-gray-300 rounded-lg resize-none focus:ring-2 focus:ring-brand-primary focus:border-transparent"
              rows={4}
            />
            <div className="flex gap-3 mt-4">
              <button
                onClick={handleUpdateWorkingStatus}
                className="flex-1 bg-brand-primary text-white py-2 px-4 rounded-lg font-medium hover:bg-brand-dark transition-colors duration-200"
              >
                Save Work Status
              </button>
              <button
                onClick={() => {
                  setShowWorkingStatusModal(false);
                  setWorkingStatus('');
                }}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors duration-200"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AttendanceSystem;